import { useEffect, useRef, useState } from "react";

import {
  BobbinMobileError,
  type BobbinMobileClient,
} from "./client";
import {
  bobbinContentText,
  bobbinNodeKind,
  bobbinNodeTitle,
  formatBobbinTime,
  redactBobbinSecrets,
} from "./presentation";
import type {
  BobbinGraphWalk,
  BobbinNode,
  BobbinNodeContent,
  BobbinSearchResult,
} from "./types";

interface BobbinSearchViewProps {
  client: BobbinMobileClient;
  csrfToken: string;
}

function safeMessage(error: unknown): string {
  if (error instanceof BobbinMobileError) return error.message;
  if (error instanceof DOMException && error.name === "AbortError") return "";
  return "Bobbin could not complete that request.";
}

export function BobbinSearchView({ client, csrfToken }: BobbinSearchViewProps) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<BobbinSearchResult[]>([]);
  const [selected, setSelected] = useState<BobbinNode | null>(null);
  const [content, setContent] = useState<BobbinNodeContent | null>(null);
  const [walk, setWalk] = useState<BobbinGraphWalk | null>(null);
  const [loading, setLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState("");
  const requestRef = useRef<AbortController | null>(null);

  useEffect(() => () => requestRef.current?.abort(), []);

  const beginRequest = () => {
    requestRef.current?.abort();
    const controller = new AbortController();
    requestRef.current = controller;
    return controller;
  };

  const search = async () => {
    if (!query.trim()) return;
    const controller = beginRequest();
    setLoading(true);
    setError("");
    setSelected(null);
    setContent(null);
    setWalk(null);
    try {
      setResults(await client.callTool(
        "search",
        { query: query.trim(), limit: 40 },
        csrfToken,
        controller.signal,
      ));
    } catch (searchError) {
      setError(safeMessage(searchError));
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  };

  const openNode = async (nodeId: string) => {
    const controller = beginRequest();
    setDetailLoading(true);
    setError("");
    try {
      const [node, nodeContent, graph] = await Promise.all([
        client.callTool("node_lookup", { node_id: nodeId }, csrfToken, controller.signal),
        client.callTool(
          "node_content",
          { node_id: nodeId, limit: 25, max_chars: 32_768 },
          csrfToken,
          controller.signal,
        ),
        client.callTool(
          "graph_walk",
          { node_id: nodeId, depth: 1, limit: 50 },
          csrfToken,
          controller.signal,
        ),
      ]);
      if (!node) {
        throw new BobbinMobileError({
          status: 404,
          code: "SPOOL_NOT_FOUND",
          retryable: false,
        });
      }
      setSelected(node);
      setContent(nodeContent);
      setWalk(graph);
    } catch (detailError) {
      setError(safeMessage(detailError));
    } finally {
      if (!controller.signal.aborted) setDetailLoading(false);
    }
  };

  const loadMoreContent = async () => {
    if (!content?.next_cursor || !selected) return;
    const controller = beginRequest();
    setDetailLoading(true);
    setError("");
    try {
      const next = await client.callTool(
        "node_content",
        {
          node_id: selected.id,
          cursor: content.next_cursor,
          limit: 25,
          max_chars: 32_768,
        },
        csrfToken,
        controller.signal,
      );
      setContent({
        ...next,
        items: [...content.items, ...next.items],
      });
    } catch (contentError) {
      setError(safeMessage(contentError));
    } finally {
      if (!controller.signal.aborted) setDetailLoading(false);
    }
  };

  const loadMoreNeighbors = async () => {
    if (!walk?.next_cursor || !selected) return;
    const controller = beginRequest();
    setDetailLoading(true);
    setError("");
    try {
      const next = await client.callTool(
        "graph_walk",
        {
          node_id: selected.id,
          depth: walk.depth,
          limit: 50,
          cursor: walk.next_cursor,
        },
        csrfToken,
        controller.signal,
      );
      const nodes = new Map(walk.nodes.map((node) => [node.id, node]));
      next.nodes.forEach((node) => nodes.set(node.id, node));
      const edges = new Map(walk.edges.map((edge) => [edge.id, edge]));
      next.edges.forEach((edge) => edges.set(edge.id, edge));
      setWalk({
        ...next,
        nodes: [...nodes.values()],
        edges: [...edges.values()],
      });
    } catch (walkError) {
      setError(safeMessage(walkError));
    } finally {
      if (!controller.signal.aborted) setDetailLoading(false);
    }
  };

  const neighbors = walk?.nodes.filter((node) => node.id !== selected?.id) ?? [];

  return (
    <section className="bobbin-mobile__view" aria-labelledby="bobbin-search-title">
      <header className="bobbin-mobile__view-header">
        <div>
          <span className="bobbin-mobile__eyebrow">Corpus</span>
          <h1 id="bobbin-search-title">Search</h1>
        </div>
      </header>

      <form
        className="bobbin-mobile__search"
        role="search"
        onSubmit={(event) => {
          event.preventDefault();
          void search();
        }}
      >
        <label className="bobbin-mobile__field">
          <span>Search all synced content</span>
          <input
            type="search"
            value={query}
            autoComplete="off"
            enterKeyHint="search"
            placeholder="Daily, notes, Thread artifacts…"
            onChange={(event) => setQuery(event.target.value)}
          />
        </label>
        <button
          className="bobbin-mobile__primary-button"
          type="submit"
          disabled={loading || !query.trim()}
        >
          {loading ? "Searching…" : "Search"}
        </button>
      </form>

      {error && <div className="bobbin-mobile__alert" role="alert">{error}</div>}

      {selected ? (
        <div className="bobbin-mobile__node-detail">
          <button
            className="bobbin-mobile__back-button"
            type="button"
            onClick={() => {
              setSelected(null);
              setContent(null);
              setWalk(null);
            }}
          >
            ← Search results
          </button>
          <article className="bobbin-mobile__detail-card">
            <header>
              <div>
                <span className="bobbin-mobile__eyebrow">{bobbinNodeKind(selected)}</span>
                <h2>{bobbinNodeTitle(selected)}</h2>
              </div>
              {selected.visibility && <span className="bobbin-mobile__pill">{selected.visibility}</span>}
            </header>
            <p className="bobbin-mobile__muted">
              Updated {formatBobbinTime(selected.updated_at ?? selected.source_updated_at)}
            </p>
            <div className="bobbin-mobile__content-blocks">
              {content?.items.length ? content.items.map((item) => (
                <div className="bobbin-mobile__content-block" key={item.content_id}>
                  <span>{item.content_kind === "content_block" ? "Block" : "Body"}</span>
                  <div className="bobbin-mobile__markdown-text">{bobbinContentText(item)}</div>
                </div>
              )) : selected.body_md ? (
                <div className="bobbin-mobile__markdown-text">{redactBobbinSecrets(selected.body_md)}</div>
              ) : (
                <div className="bobbin-mobile__empty">No readable content.</div>
              )}
            </div>
            {content?.next_cursor && (
              <button
                className="bobbin-mobile__secondary-button"
                type="button"
                disabled={detailLoading}
                onClick={() => void loadMoreContent()}
              >
                {detailLoading ? "Loading…" : "Load more content"}
              </button>
            )}
          </article>

          <section className="bobbin-mobile__neighbors" aria-labelledby="bobbin-neighbors-title">
            <header>
              <h2 id="bobbin-neighbors-title">Graph neighbors</h2>
              <span>{walk?.edges.length ?? 0} links</span>
            </header>
            {neighbors.length ? (
              <div className="bobbin-mobile__card-list">
                {neighbors.map((node) => (
                  <button
                    className="bobbin-mobile__result-card"
                    type="button"
                    key={node.id}
                    onClick={() => void openNode(node.id)}
                  >
                    <span className="bobbin-mobile__eyebrow">{bobbinNodeKind(node)}</span>
                    <strong>{bobbinNodeTitle(node)}</strong>
                    <span>Explore neighbor →</span>
                  </button>
                ))}
              </div>
            ) : (
              <div className="bobbin-mobile__empty">No visible neighbors.</div>
            )}
            {walk?.next_cursor && (
              <button
                className="bobbin-mobile__secondary-button"
                type="button"
                disabled={detailLoading}
                onClick={() => void loadMoreNeighbors()}
              >
                {detailLoading ? "Loading…" : "Load more neighbors"}
              </button>
            )}
          </section>
        </div>
      ) : detailLoading ? (
        <div className="bobbin-mobile__empty">Loading node and graph…</div>
      ) : results.length ? (
        <div className="bobbin-mobile__card-list" aria-live="polite">
          {results.map((result) => (
            <button
              className="bobbin-mobile__result-card"
              type="button"
              key={result.id}
              onClick={() => void openNode(result.id)}
            >
              <span className="bobbin-mobile__eyebrow">{bobbinNodeKind(result)}</span>
              <strong>{bobbinNodeTitle(result)}</strong>
              {result.snippet && (
                <span className="bobbin-mobile__result-snippet">
                  {redactBobbinSecrets(result.snippet)}
                </span>
              )}
            </button>
          ))}
        </div>
      ) : query && !loading ? (
        <div className="bobbin-mobile__empty">No synced results.</div>
      ) : (
        <div className="bobbin-mobile__empty">Search stays in memory and clears when Bobbin is backgrounded.</div>
      )}
    </section>
  );
}
